import type * as React from 'react';
import { graphql } from 'react-apollo';

import _ from 'lodash';
import { compose, withProps } from 'recompose';

import AccomplishmentsRecommendationCollectionQuery from 'bundles/accomplishments/api/AccomplishmentsRecommendationCollectionQuery.graphql';
import type {
  AccomplishmentsRecommendationCollectionQuery as AccomplishmentsRecommendationCollectionQueryData,
  AccomplishmentsRecommendationCollectionQueryVariables,
} from 'bundles/accomplishments/api/__generated__/AccomplishmentsRecommendationCollectionQuery';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { DISPLAY_POLICY } from 'bundles/accomplishments/api/accomplishmentsAndPaymentsRecommendationSwitcher';
import type { DiscoveryCollection } from 'bundles/browse-common/legacy/types/DiscoveryCollections';
import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import { mapRecommendationCollectionForCarousel } from 'bundles/collections-carousel/utils/recommendationCollectionMappingUtils';

import _t from 'i18n!nls/accomplishments';

type InputProps = { recommendationDisplayPolicy?: string; hasNonEmptyCards?: boolean };
type GraphqlProps = InputProps;
type GraphqlData = AccomplishmentsRecommendationCollectionQueryData;
type GraphqlVariables = AccomplishmentsRecommendationCollectionQueryVariables;
interface upNextCollections {
  id?: string;
  entities?: {
    id?: string;
  }[];
  label?: string;
}
type GraphqlChildProps = {
  recommendationCollections: CarouselCollection[];
  loading?: boolean;
  refetch?: () => void;
  completedCourseList?: string[];
  courseData?: {
    id: string;
    title: string;
    imageSrc: string;
    partners: { elements: { squareLogo: string; name: string }[] };
  }[];
  s12nData?: {
    s12nId: string;
    s12nName: string;
    s12nRemainingCourses: string[];
  }[];
};
type fromWithProps = {
  loadingRecommendations?: boolean;
  recommendationCollections?: Array<upNextCollections | DiscoveryCollection | CarouselCollection | null>;
  reloadRecommendations?: () => void;
};
export type withAccomplishmentsAndPaymentsRecommendationDataProps = fromWithProps & GraphqlChildProps;

const NUM_RECOMMENDATIONS = 12;

export const recommendationCollectionIdSelector = (recommendationDisplayPolicy?: string) =>
  recommendationDisplayPolicy === DISPLAY_POLICY.COMPLETED_COURSE ? 'rt-recs-primary-prod' : 'rt-recs-free-prod';

export const mapRecommendationCollectionsForCarousel = (
  recommendations?: GraphqlData['Recommendations'] | null
): CarouselCollection[] | undefined => {
  if (!recommendations) return undefined;
  return [recommendations.recentlyViewed, recommendations.queryCollection]
    .map((collection) => mapRecommendationCollectionForCarousel(collection))
    .filter((collection): collection is CarouselCollection => !!collection?.entities.length);
};

export const withPropsFunc = ({
  s12nData,
  courseData,
  loading,
  recommendationCollections = [],
  completedCourseList,
  refetch,
}: GraphqlChildProps): fromWithProps => {
  const s12nDataLength = s12nData?.length || 0;
  const courseDataLength = courseData?.length || 0;
  const s12nDataEmpty = !(s12nDataLength > 0);
  const courseDataEmpty = !(courseDataLength > 0);

  if (s12nDataEmpty || courseDataEmpty) {
    return {};
  }

  const newS12nData: {
    s12nId: string;
    s12nName: string;
    s12nRemainingCourses: string[];
  }[] = [];

  completedCourseList?.forEach((completedCourse) => {
    s12nData?.forEach((s12nObj) => {
      if (s12nObj.s12nRemainingCourses.includes(completedCourse)) {
        // removes the completed course
        const newS12nObj = _.cloneDeep(s12nObj);
        newS12nObj.s12nRemainingCourses.splice(s12nObj.s12nRemainingCourses.indexOf(completedCourse), 1);
        newS12nData.push(newS12nObj);
      }
    });
  });

  const upNextCollections: {
    debug?: null;
    s12ns?: null;
    __typename?: string;
    id?: string;
    entities?: {
      id?: string;
    }[];
    label?: string;
  }[] = [];

  newS12nData?.forEach((s12nBlock) => {
    const finalObj: {
      id?: string;
      entities?: {
        id?: string;
        name?: string;
        imageUrl?: string;
        partners?: {
          logo?: string;
        }[];
        productType?: string;
        __typename?: string;
      }[];
      label?: string;
    } = {};

    finalObj.entities = [];

    if (upNextCollections.length > 3) {
      return;
    }
    finalObj.id = `${s12nBlock.s12nName}-${s12nBlock.s12nId}:up-next$no_attribute$no_attribute`;

    s12nBlock.s12nRemainingCourses.forEach((id) => {
      const course = courseData?.find((courseObj) => courseObj.id === id);
      finalObj?.entities?.push({
        id,
        ...course,
        name: course?.title,
        imageUrl: course?.imageSrc || '',
        partners:
          course?.partners?.elements?.map((partner) => ({
            ...partner,
            logo: partner.squareLogo,
          })) || [],
        productType: 'COURSE',
        __typename: 'DiscoveryCollections_course',
      });
    });

    finalObj.label = _t(`Up Next in #{name} Specialization`, { name: `${s12nBlock.s12nName}` });

    upNextCollections.push(finalObj);
  });

  return {
    // push into recommendation collection in the proper format
    loadingRecommendations: loading,
    recommendationCollections: [...upNextCollections, ...recommendationCollections],
    reloadRecommendations: () => {
      if (refetch) refetch();
    },
  };
};

export default <
  InnerProps extends withAccomplishmentsAndPaymentsRecommendationDataProps,
  OuterProps extends InputProps,
>(
  Component: React.ComponentType<InnerProps>
) =>
  compose<InnerProps, OuterProps>(
    graphql<GraphqlProps, GraphqlData, GraphqlVariables, GraphqlChildProps>(
      AccomplishmentsRecommendationCollectionQuery,
      {
        options: ({ recommendationDisplayPolicy }) => ({
          variables: {
            queryCollectionId: recommendationCollectionIdSelector(recommendationDisplayPolicy),
            limit: NUM_RECOMMENDATIONS,
          },
          ssr: true,
          context: { clientName: 'gatewayGql' },
        }),
        props: ({ data, ownProps }, lastProps) => {
          const currentPropsValue = mapRecommendationCollectionsForCarousel(data?.Recommendations);
          const lastPropsValue =
            lastProps && 'recommendationCollections' in lastProps && lastProps?.recommendationCollections;

          return {
            ...ownProps,
            recommendationCollections: currentPropsValue || lastPropsValue || [],
            loading: data?.loading,
            refetch: data?.refetch,
          };
        },
        // skip: ({ recommendationDisplayPolicy, courseData }) => !recommendationDisplayPolicy && courseData.length > 0,
      }
    ),
    withProps<fromWithProps, GraphqlChildProps>(withPropsFunc)
  )(Component);
